import { Button, Collapse, Dialog, DialogBody, DialogFooter } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { AutoSuggest, SelectItem } from '@zaiusinc/hera';
import React from 'react';
import DataSyncCgObjectsStore from '../../../../stores/object/DataSyncCgObjectsStore';
import { useObserver } from 'mobx-react-lite';
import { mainLanguages, searchLocales } from '../../../../lib/localeCatalog';
import styles from './CGLanguageSelectDialog.module.sass';

const SUPPORTED_LOCALES_DOC =
  'https://docs.developers.optimizely.com/platform-optimizely/docs/locale#supported-locales-with-language-analysis';
const LOCALIZATION_DOC = 'https://docs.developers.optimizely.com/platform-optimizely/docs/locale';

interface Props {
  existingLanguages: SelectItem[];
  isOpen: boolean;
  dataSyncCgObjectStore: DataSyncCgObjectsStore;
  onSave: (value: SelectItem) => void;
  onClose: () => void;
}

export const CGLanguageSelectDialog = (props: Props) => {

  const [selected, setSelected] = React.useState<SelectItem>();
  const [query, setQuery] = React.useState('');
  const [helpOpen, setHelpOpen] = React.useState(false);

  const excludeValues = React.useMemo(
    () => props.existingLanguages.map((language) => language.value),
    [props.existingLanguages]
  );

  // Empty box shows the curated main languages; typing runs a string-contains lookup
  // over the full locale-codes catalog. We pre-filter here, so the AutoSuggest predicate
  // is a pass-through.
  const suggestions = React.useMemo(() => (
    query.trim() ? searchLocales(query, excludeValues) : mainLanguages(excludeValues)
  ), [query, excludeValues]);

  const saveLanguage = () => {
    if (selected) {
      props.onSave(selected);
    }
  };

  return useObserver(() => (
    <Dialog title="Add New Language" isOpen={props.isOpen} onClose={props.onClose}>
      <DialogBody>
        <AutoSuggest
          fill
          items={suggestions}
          selectedItem={selected ?? null}
          itemPredicate={() => true}
          onQueryChange={(value: string) => setQuery(value)}
          onItemSelect={(item: SelectItem) => setSelected(item)}
          inputProps={{placeholder: 'Search a language or enter a locale, e.g. en-US'}}
        />
        <div className={styles.helpSection}>
          <Button
            minimal
            small
            className={styles.helpToggle}
            icon={helpOpen ? IconNames.CHEVRON_DOWN : IconNames.CHEVRON_RIGHT}
            text="About Optimizely Graph locales"
            onClick={() => setHelpOpen((open) => !open)}
          />
          <Collapse isOpen={helpOpen}>
            <div className={styles.help}>
              <ul className={styles.helpList}>
                <li>
                  Graph routes each locale to its own index and applies language-specific analysis
                  (tokenization, decompounding, and stemming) that powers full-text search.
                </li>
                <li>
                  The two-letter language code (e.g. en) selects that analysis. Add a country code
                  (e.g. en-US) to index a regional version separately.
                </li>
                <li>
                  Supported locales get language-specific analysis; other locales are still indexed
                  and searchable.
                </li>
                <li>Use NEUTRAL for language-neutral content such as assets.</li>
              </ul>
              <p className={styles.helpLinks}>
                Learn more about{' '}
                <a href={SUPPORTED_LOCALES_DOC} target="_blank" rel="noopener noreferrer">supported locales</a>
                {' and '}
                <a href={LOCALIZATION_DOC} target="_blank" rel="noopener noreferrer">localization in Graph</a>.
              </p>
            </div>
          </Collapse>
        </div>
      </DialogBody>
      <DialogFooter actions={<Button loading={props.dataSyncCgObjectStore.savingNewLanguage} intent="primary" text="Save"
        disabled={!selected}
        onClick={saveLanguage}/>} />
    </Dialog>
  ));
};
